import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import {
  selectFlowType,
  selectUuid,
  selectVeteranContactEmail,
  selectInvitationData,
} from '../redux/slices/formSlice';
import { FLOW_TYPES, URLS } from '../utils/constants';
import {
  buildInvitationSearch,
  parseInvitationFromSearch,
} from '../utils/invitationQuery';

/**
 * HOC that restricts route access based on the user's current flow type.
 *
 * This prevents users from switching between schedule and cancel flows:
 * - Users in the 'schedule' flow cannot access cancel-only routes
 * - Users in the 'cancel' flow cannot access schedule-only routes
 * - Routes marked as 'any' are accessible from both flows
 *
 * @param {React.Component} Component - The component to wrap
 * @param {'schedule'|'cancel'|'any'} allowedFlow - Which flow can access this route
 * @returns {React.Component} - The wrapped component with flow guard logic
 */
const withFlowGuard = (Component, allowedFlow = FLOW_TYPES.ANY) => {
  const WrappedComponent = props => {
    const navigate = useNavigate();
    const location = useLocation();
    const flowType = useSelector(selectFlowType);
    const uuid = useSelector(selectUuid);
    const veteranContactEmail = useSelector(selectVeteranContactEmail);
    const invitationData = useSelector(selectInvitationData);

    const invitationFromUrl = useMemo(
      () => parseInvitationFromSearch(location.search),
      [location.search],
    );

    // allowedFlow comes from the HOC factory (constant per wrapper); omit from deps.
    useEffect(() => {
      // Skip guard if route allows any flow
      if (allowedFlow === FLOW_TYPES.ANY) return;

      // Skip guard if flow type hasn't been set yet (user just started)
      if (!flowType || flowType === FLOW_TYPES.ANY) return;

      // If user's flow doesn't match the allowed flow, redirect to start
      if (flowType !== allowedFlow) {
        const effectiveUuid = uuid ?? invitationFromUrl.uuid;
        const effectiveEmail =
          veteranContactEmail ?? invitationFromUrl.veteranContactEmail;
        const effectiveData = invitationData ?? invitationFromUrl.data;
        const query = buildInvitationSearch({
          uuid: effectiveUuid,
          veteranContactEmail: effectiveEmail,
          data: effectiveData,
          cancel: flowType === FLOW_TYPES.CANCEL,
        });
        navigate(query ? `${URLS.VERIFY}${query}` : URLS.VERIFY, {
          replace: true,
        });
      }
    }, [
      flowType,
      navigate,
      uuid,
      veteranContactEmail,
      invitationData,
      invitationFromUrl.uuid,
      invitationFromUrl.veteranContactEmail,
      invitationFromUrl.data,
    ]);

    // Don't render while redirect is pending (wrong flow detected)
    if (
      allowedFlow !== FLOW_TYPES.ANY &&
      flowType &&
      flowType !== FLOW_TYPES.ANY &&
      flowType !== allowedFlow
    ) {
      return null;
    }

    return <Component {...props} />;
  };

  // Set display name for debugging
  WrappedComponent.displayName = `withFlowGuard(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WrappedComponent;
};

export default withFlowGuard;
